import { CustomIdp, SocialIdp } from './constants';

import { Capacitor } from '@capacitor/core';
import { ResourcesConfig } from 'aws-amplify';
import { brandOverrides } from '@/config/brandConfig';
import config from '../../capacitor.config';

type CustomProvider = {
  custom: CustomIdp;
};

const redirect = Capacitor.isNativePlatform()
  ? `capacitor://${config.appId}`
  : window.location.origin;

export const customIdpNames: CustomIdp[] = JSON.parse(
  import.meta.env.VITE_AUTH_USER_POOL_CUSTOM_IDPS || '[]',
);

// Typed for Amplify
const providers: CustomProvider[] = customIdpNames.map((p: CustomIdp) => ({
  custom: p,
}));

// Read from the brand's own overrides rather than the merged config in
// @/config. That module imports defaultConfig, which imports this one, so
// reading the merged config here reads a module that has not finished
// initialising and whose default export is still undefined. The sign-in flags
// default to false, so an override set to true is the merged value.
const hasOAuthProviders =
  providers.length > 0 ||
  brandOverrides.app?.useGoogleSignIn === true ||
  brandOverrides.app?.useAppleSignIn === true;

export const awsConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
      identityPoolId: import.meta.env.VITE_AUTH_IDENTITY_POOL_ID || '',

      // OPTIONAL - Amazon Cognito User Pool ID
      // (User pools > General Settings > Pool Id)
      userPoolId: import.meta.env.VITE_AUTH_USER_POOL_ID || '',

      // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
      // (User pools > General Settings > App clients > App client id)
      userPoolClientId: import.meta.env.VITE_AUTH_POOL_WEB_CLIENT_ID || '',

      // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
      allowGuestAccess: false,
      loginWith: hasOAuthProviders
        ? {
            oauth: {
              domain: import.meta.env.VITE_AUTH_USER_POOL_DOMAIN || '',
              scopes: [
                'openid',
                'email',
                'profile',
                'aws.cognito.signin.user.admin',
              ],
              redirectSignIn: [`${redirect}/sso`],
              redirectSignOut: [`${redirect}/signin`],
              responseType: 'code', // For Authorization Code Grant
              providers,
            },
          }
        : undefined,
    },
  },
};

export const getSsoUrl = (identityProvider: CustomIdp | SocialIdp) => {
  const cognitoConfig = awsConfig.Auth?.Cognito;
  const base = `https://${cognitoConfig?.loginWith?.oauth?.domain}/oauth2/authorize?`;
  const responseType = `response_type=${cognitoConfig?.loginWith?.oauth?.responseType}&`;
  const client = `client_id=${cognitoConfig?.userPoolClientId}&`;
  const state = `state=${crypto.randomUUID()}&`;
  const redirectUrl = `redirect_uri=${cognitoConfig?.loginWith?.oauth?.redirectSignIn[0]}&`;
  const scope = `scope=${cognitoConfig?.loginWith?.oauth?.scopes.join('%20')}&`;
  // Cognito names its Apple provider SignInWithApple. signInWithRedirect maps
  // Amplify's 'Apple' to it; a hand-built authorize URL has to do the same.
  const cognitoIdp =
    identityProvider === SocialIdp.APPLE ? 'SignInWithApple' : identityProvider;
  const provider = `identity_provider=${cognitoIdp}`;
  return `${base}${responseType}${client}${state}${redirectUrl}${scope}${provider}`;
};
